import React from 'react';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import kebabCase from 'lodash/kebabCase';
import { Field as ReduxField } from 'redux-form/immutable';
import { Field as FinalFormField } from 'react-final-form';
import { omitBy } from 'lodash';

import { GenericContext } from 'shared/constants';
import {
  composeValidators,
  isFinalFormContext,
  logError,
  stringifyError,
  toQueryString,
  typeaheadResultsPlaceholder,
} from 'shared/functions';

import { requestBackend } from 'utils/request';

import type { GenericContextType } from 'types/generic-context';
import type { FieldTypeaheadProps } from './types';

import { RenderSelect } from '../RenderSelect';

import {
  creatibleOption,
  getCreatibleFilterOptions,
  getCreatibleValueRenderer,
  getExclude,
  notSelectableOption,
  SingleValue,
  typeaheadStyles,
} from './functions';

export function FieldTypeahead<ItemDto>(props: FieldTypeaheadProps<ItemDto>) {
  const {
    input,
    meta,
    options,
    creatible,
    clearable,
    name: propsName,
    entityType,
    items: propsItems,
    entitiesType,
    endpointPrefix = '',
    formClass,
    labelKey,
    valueKey,
    labelClass,
    valueClass,
    validate,
    label,
    limit,
    creatiblePattern,
    searchPath,
    selectSingle,
    itemsGetter,
    itemsFilter,
    filterOption,
    filterValue,
    valueRenderer,
    optionRenderer,
    formatOptionLabel,
    onChangeValue,
    disabled,
    isLoading,
    multi,
    placeholder,
    postfix,
    postfixClass,
    postfixAddonClass,
    postfixOnClick,
    renderCreatibleOption = creatibleOption,
    isFixedOption,
  } = props;

  const { isAllowedOperation } = React.useContext(GenericContext) as GenericContextType;

  const [items, setItems] = React.useState(propsItems);
  const [loadingItems, setLoadingItems] = React.useState(false);
  const [creatibleAndAllowed] = React.useState(creatible && isAllowedOperation(`${entityType}-create`));
  const [value, setValue] = React.useState(
    // @ts-expect-error this code is correct
    input?.value?.toJS ? input.value.toJS() : input?.value || null,
  );
  const [itemName, setItemName] = React.useState('');
  const [stringifiedSearchPath, setStringifiedSearchPath] = React.useState('');
  const [error, setError] = React.useState('');
  const abortRef = React.useRef<AbortController | null>(null);
  const processGetItemsResponse = React.useCallback(
    (name) => (response: any) => {
      const newItems = itemsGetter ? itemsGetter(response, name) : response.items;
      const singleSelected = !name && newItems.length === 1 && selectSingle ? newItems[0] : null;
      if (creatibleAndAllowed) {
        const lowerCasedName = name.toLowerCase();

        const findByLabel = (item: ItemDto) => get(item, labelKey).toLowerCase() === lowerCasedName;

        const validLabel = !creatiblePattern || name.match(creatiblePattern);
        const foundInCurrent = Array.isArray(value) && value.find(findByLabel);
        const foundInResponse = newItems.find(findByLabel);

        if (name && validLabel && !foundInCurrent && !foundInResponse) {
          newItems.unshift(renderCreatibleOption(entityType, valueKey, labelKey, name));
        }

        if (!newItems.length) {
          newItems.push(notSelectableOption(valueKey, labelKey, 'Not found'));
        }
      }
      setItems(newItems);
      setLoadingItems(false);
      if (singleSelected) {
        selectSingle(singleSelected);
      }
    },
    [
      creatibleAndAllowed,
      creatiblePattern,
      entityType,
      itemsGetter,
      labelKey,
      selectSingle,
      value,
      valueKey,
      renderCreatibleOption,
    ],
  );

  const processGetItemsError = React.useCallback((err) => {
    const isAbort = err.message === 'AbortError';
    if (isAbort) return;
    setLoadingItems(false);
    setError(stringifyError(err));
  }, []);

  const setCreatibleOption = React.useCallback(
    (name) => {
      setItems(creatibleAndAllowed && name ? [renderCreatibleOption(entityType, valueKey, labelKey, name)] : []);
    },
    [creatibleAndAllowed, entityType, labelKey, valueKey, renderCreatibleOption],
  );

  const getPath = React.useCallback(
    (name) => {
      if (!entitiesType && !searchPath) {
        throw new Error('Either entitiesType or searchPath should be present');
      }
      const queryOptions = {
        [labelKey || 'name']: name || '',
        exclude: getExclude(value, valueKey),
      };
      if (limit) {
        queryOptions.limit = limit;
      }
      const truthyQueryOptions = omitBy(queryOptions, (item) => !item);
      const [path, options] = searchPath
        ? searchPath(name, value)
        : [`${endpointPrefix}/${kebabCase(entitiesType)}?${toQueryString(truthyQueryOptions)}`];
      return [path, options] as const;
    },
    [endpointPrefix, entitiesType, labelKey, limit, searchPath, value, valueKey],
  );

  const getItems = React.useCallback(
    (name) => {
      setLoadingItems(true);
      setError('');

      const path = getPath(name);

      if (!path) {
        setCreatibleOption(name);
        return setLoadingItems(false);
      } else if (typeof path === 'string') {
        logError(path);
        throw new Error('searchPath should not return a string');
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();
      const [url, request = {}] = path;
      return requestBackend(url, {
        ...request,
        signal: abortRef.current.signal,
      }).then(processGetItemsResponse(name), processGetItemsError);
    },
    [getPath, processGetItemsError, processGetItemsResponse, setCreatibleOption],
  );

  const creatibleFilterOptions = React.useCallback(
    () => getCreatibleFilterOptions(multi, valueKey, labelKey, options, entityType),
    [multi, valueKey, labelKey, options, entityType],
  );

  const creatibleOptionRenderer = (option: any) =>
    option.specialLabel || (optionRenderer ? optionRenderer(option) : get(option, labelKey));

  const onChangeValueHandler = React.useCallback(
    (newValue) => {
      setTimeout(() => {
        if (newValue === value) {
          return;
        }
        setValue(newValue);
        if (onChangeValue) {
          onChangeValue(newValue);
        }
      });
    },
    [value, onChangeValue],
  );

  const onInputChange = debounce((newItemName = '') => {
    if (options) {
      return;
    } else if (newItemName === itemName) {
      return;
    }
    setItemName(newItemName);
    setItems([]);
    getItems(newItemName);
  }, 500);

  React.useEffect(() => {
    if (loadingItems) {
      return;
    }
    if (searchPath) {
      const compiledSearchPath = searchPath('', null);
      const newStringifiedSearchPath =
        typeof compiledSearchPath === 'object' ? JSON.stringify(compiledSearchPath) : compiledSearchPath;
      if (stringifiedSearchPath !== newStringifiedSearchPath) {
        setStringifiedSearchPath(newStringifiedSearchPath);
        setItems([]);
        getItems('');
      }
      return;
    }
    if (!options && !items) {
      getItems('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getItems, items, options, searchPath, stringifiedSearchPath]);

  const name = input?.name || propsName;
  const finalFormContext = isFinalFormContext();
  const FieldComponent: any = finalFormContext ? FinalFormField : ReduxField;
  const metaProps = meta ? { meta } : {}; // Can be used as component={} or directly

  if (creatible && !entitiesType) {
    logError('entitiesType for creatible FieldTypeahead');
  }

  const filteredOptions = React.useMemo(() => {
    const opts = options || items;
    return typeof itemsFilter === 'function' ? itemsFilter(opts) : opts;
  }, [itemsFilter, items, options]);

  return (
    <div>
      <FieldComponent
        id={name}
        name={name}
        {...metaProps}
        component={RenderSelect}
        label={'label' in props ? label : translate(startCase(entityType)) || '(Label is missing)'}
        placeholder={placeholder || translate(options ? 'Select...' : 'Start typing...')}
        noResultsText={typeaheadResultsPlaceholder(loadingItems, !itemName)}
        isLoading={loadingItems || isLoading}
        options={filteredOptions}
        formClass={formClass}
        valueKey={valueKey}
        labelKey={labelKey}
        labelClass={labelClass}
        disabled={disabled}
        valueClass={valueClass}
        validate={finalFormContext && Array.isArray(validate) ? composeValidators(...validate) : validate}
        isFixedOption={isFixedOption}
        onInputChange={onInputChange}
        optionRenderer={creatibleOptionRenderer}
        valueRenderer={getCreatibleValueRenderer(valueRenderer, labelKey)}
        formatOptionLabel={formatOptionLabel}
        styles={typeaheadStyles}
        onSelectResetsInput={false}
        onBlurResetsInput={false}
        onChangeValue={onChangeValueHandler}
        filterOptions={creatibleAndAllowed ? creatibleFilterOptions : undefined}
        filterOption={filterOption}
        filterValue={filterValue}
        multi={multi}
        postfix={postfix}
        postfixClass={postfixClass}
        postfixAddonClass={postfixAddonClass}
        postfixOnClick={postfixOnClick}
        components={creatible ? <SingleValue /> : null}
        typeahead
        clearable={clearable}
      />
      {error && <div className="text-danger">{error}</div>}
    </div>
  );
}
